import React, { useState, useEffect } from 'react';
import { BarChart3, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Info, Download, Loader2, Sparkles, Settings, Save } from 'lucide-react';
import { jsPDF } from 'jspdf';
import IntelligentUnitPrediction from './IntelligentUnitPrediction';
import { useNotification } from '../context/NotificationContext';
import { useMediaQuery } from '../hooks/useMediaQuery';

const normalizeRiskLevel = (riskLevel) => {
  const risk = String(riskLevel || 'unknown').toLowerCase();
  return ['critical', 'high', 'medium', 'low'].includes(risk) ? risk : 'unknown';
};

const hasDetailedPredictionPayload = (prediction) => {
  if (!prediction || typeof prediction !== 'object') return false;
  const level = String(prediction.detail_level || '').toLowerCase();
  if (level === 'summary' || level === 'advisory') return true;
  return (
    Array.isArray(prediction.predicted_issues) ||
    Boolean(prediction.timeline_estimate) ||
    Boolean(prediction.maintenance_checks)
  );
};

const PredictionsDashboard = ({
  unitPredictions,
  unitPredictionMeta = {},
  fetchIntelligentUnitPrediction,
  seasonalAnalysis,
  availableUnits = [],
  loadingStates = {},
  uploadToStorage,
  onUsePrompt
}) => {
  const { notify } = useNotification();
  // Use the first available unit when data arrives
  const [selectedUnit, setSelectedUnit] = useState('');
  const [showMethodology, setShowMethodology] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSavingToStorage, setIsSavingToStorage] = useState(false);
  const [dataRangeLabel, setDataRangeLabel] = useState('Aug 2024 - Dec 2025');
  const [activeRiskFilter, setActiveRiskFilter] = useState('all');
  const [showAllUnitsMobile, setShowAllUnitsMobile] = useState(false);
  const [seasonalSlide, setSeasonalSlide] = useState(0);
  const isMobile = useMediaQuery('(max-width: 640px)');

  // Set initial selected unit when availableUnits loads
  React.useEffect(() => {
    if (availableUnits.length > 0 && !selectedUnit) {
      setSelectedUnit(availableUnits[0]);
    }
  }, [availableUnits, selectedUnit]);

  useEffect(() => {
    const getRangeFromCache = () => {
      try {
        const cached = localStorage.getItem('stats_overview_cache');
        if (!cached) return;
        const parsed = JSON.parse(cached);
        const data = parsed?.data || parsed;
        const start = data?.date_range_start;
        const end = data?.date_range_end;
        if (start && end && start !== 'Unknown' && end !== 'Unknown') {
          const startLabel = new Date(start).toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'Australia/Brisbane' });
          const endLabel = new Date(end).toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'Australia/Brisbane' });
          setDataRangeLabel(`${startLabel} - ${endLabel}`);
        }
      } catch (_) {
        // Ignore cache parsing errors
      }
    };

    getRangeFromCache();
    const handleStorage = (e) => {
      if (e.key === 'stats_overview_cache') {
        getRangeFromCache();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (!selectedUnit || activeRiskFilter === 'all') return;
    const selectedRisk = normalizeRiskLevel(unitPredictions[selectedUnit]?.risk_level);
    if (selectedRisk === activeRiskFilter) return;

    const matchingUnit = availableUnits.find(
      (unit) => normalizeRiskLevel(unitPredictions[unit]?.risk_level) === activeRiskFilter
    );
    if (matchingUnit) setSelectedUnit(matchingUnit);
  }, [activeRiskFilter, availableUnits, selectedUnit, unitPredictions]);

  // Prioritize selected unit fetch so detail panel does not wait behind background queue loads.
  useEffect(() => {
    if (!selectedUnit) return;
    const selectedPrediction = unitPredictions[selectedUnit];
    const meta = unitPredictionMeta[selectedUnit] || {};
    const status = String(meta.status || '');
    const hasSummary = hasDetailedPredictionPayload(selectedPrediction);

    if (hasSummary) return;
    if (['queued', 'loading_summary', 'loading_advisory', 'retry_wait', 'error_transient', 'error_terminal'].includes(status)) return;

    fetchIntelligentUnitPrediction(selectedUnit, false, 'summary');
  }, [selectedUnit, unitPredictions, unitPredictionMeta, fetchIntelligentUnitPrediction]);

  // PDF Generation Logic (Refactored to return blob or save file)
  const createPDFBlob = () => {
    const predictionData = unitPredictions[selectedUnit];
    if (!predictionData) return null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = 20;
    const lineHeight = 7;

    // Helper to decode HTML entities and clean text
    const cleanText = (text) => {
      if (!text) return '';
      let cleaned = String(text).trim();
      cleaned = cleaned.replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ').trim();
      return cleaned;
    };

    // Helper to check if we need a new page
    const checkNewPage = (neededSpace) => {
      if (yPos + neededSpace > 270) {
        doc.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    // Title
    doc.setFillColor(220, 38, 38); // Red color
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Unplanned Maintenance Predictions & Advisory', margin, 18);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Unit: ${selectedUnit} | Generated: ${new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane' })}`, margin, 28);

    yPos = 45;
    doc.setTextColor(0, 0, 0);

    // Risk Assessment Section
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 25, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Risk Assessment', margin + 5, yPos + 10);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const riskLevel = predictionData.risk_level || 'Unknown';
    const confidence = Number.isFinite(predictionData.confidence_score)
      ? Math.round(predictionData.confidence_score * 100)
      : null;
    doc.text(`Risk Level: ${riskLevel}`, margin + 5, yPos + 18);
    doc.text(`Confidence Score: ${confidence !== null ? `${confidence}%` : 'N/A'}`, margin + 80, yPos + 18);
    yPos += 35;

    // Predicted Events
    if (predictionData.predicted_issues && predictionData.predicted_issues.length > 0) {
      checkNewPage(50);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Predicted Events (${predictionData.predicted_issues.length} total)`, margin, yPos);
      yPos += 10;

      predictionData.predicted_issues.forEach((issue, index) => {
        checkNewPage(25);
        doc.setFillColor(255, 250, 240);
        doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 20, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const probability = Math.round((issue.probability || 0) * 100);
        const issueType = cleanText(issue.issue_type) || 'Unknown Event';
        const pattern = cleanText(issue.pattern) || 'N/A';
        doc.text(`${index + 1}. ${issueType}`, margin + 3, yPos + 3);
        doc.setFont('helvetica', 'normal');
        doc.text(`Severity: ${issue.severity || 'N/A'} | Probability: ${probability}%`, margin + 3, yPos + 10);
        doc.text(`Pattern: ${pattern}`, margin + 3, yPos + 16);
        yPos += 25;
      });
      yPos += 5;
    }

    // Maintenance Checks
    if (predictionData.maintenance_checks && !predictionData.maintenance_checks.error) {
      checkNewPage(40);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Maintenance Status', margin, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Current SMU: ${predictionData.maintenance_checks.current_smu?.toLocaleString() || 'N/A'} hrs`, margin, yPos);
      yPos += lineHeight;
      doc.text(`Status: ${predictionData.maintenance_checks.maintenance_status || 'N/A'}`, margin, yPos);
      yPos += 12;

      // Next Major Service
      if (predictionData.maintenance_checks.next_major_service) {
        checkNewPage(25);
        doc.setFillColor(219, 234, 254);
        doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 18, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text('NEXT MAJOR SERVICE', margin + 3, yPos + 3);
        doc.setFont('helvetica', 'normal');
        const nextServiceDesc = cleanText(predictionData.maintenance_checks.next_major_service.description) || 'Service';
        doc.text(`${nextServiceDesc} - Due at ${predictionData.maintenance_checks.next_major_service.due_at_smu?.toLocaleString()} hrs (${predictionData.maintenance_checks.next_major_service.smu_remaining} hrs remaining)`, margin + 3, yPos + 12);
        doc.setTextColor(0, 0, 0);
        yPos += 25;
      }

      // Overdue Checks
      if (predictionData.maintenance_checks.overdue_checks?.length > 0) {
        checkNewPage(30);
        doc.setFillColor(254, 226, 226);
        doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(185, 28, 28);
        doc.text(`OVERDUE CHECKS (${predictionData.maintenance_checks.overdue_checks.length} items)`, margin + 3, yPos + 3);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        doc.setFont('helvetica', 'normal');
        predictionData.maintenance_checks.overdue_checks.forEach((item) => {
          checkNewPage(20);
          const description = cleanText(item.description) || 'No description';
          const fullText = `${description} - ${item.smu_overdue || 0} hrs overdue (${item.severity || 'N/A'})`;
          const descText = doc.splitTextToSize(fullText, pageWidth - 2 * margin - 20);

          descText.forEach((line, lineIndex) => {
            checkNewPage(10);
            const prefix = lineIndex === 0 ? '* ' : '  ';
            doc.text(prefix + line, margin + 5, yPos);
            yPos += lineHeight;
          });
          yPos += 2;
        });
        yPos += 5;
      }

      // Upcoming Maintenance
      if (predictionData.maintenance_checks.upcoming_maintenance?.length > 0) {
        checkNewPage(30);
        doc.setFillColor(254, 243, 199);
        doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(146, 64, 14);
        doc.text(`UPCOMING MAINTENANCE (${predictionData.maintenance_checks.upcoming_maintenance.length} items)`, margin + 3, yPos + 3);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        doc.setFont('helvetica', 'normal');
        predictionData.maintenance_checks.upcoming_maintenance.forEach((item) => {
          checkNewPage(20);
          const description = cleanText(item.description) || 'No description';
          const dueAtSmu = item.due_at_smu ? item.due_at_smu.toLocaleString() : 'N/A';
          const smuRemaining = item.smu_remaining || 0;
          const urgency = item.urgency || 'N/A';
          const fullText = `${description} - Due at ${dueAtSmu} hrs (${smuRemaining} hrs remaining) [${urgency}]`;
          const descText = doc.splitTextToSize(fullText, pageWidth - 2 * margin - 20);

          descText.forEach((line, lineIndex) => {
            checkNewPage(10);
            const prefix = lineIndex === 0 ? '* ' : '  ';
            doc.text(prefix + line, margin + 5, yPos);
            yPos += lineHeight;
          });
          yPos += 2;
        });
        yPos += 5;
      }

      // Checks Made
      if (predictionData.maintenance_checks.recent_maintenance?.length > 0) {
        checkNewPage(30);
        doc.setFillColor(220, 252, 231);
        doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(22, 101, 52);
        doc.text(`CHECKS MADE (${predictionData.maintenance_checks.recent_maintenance.length} completed)`, margin + 3, yPos + 3);
        doc.setTextColor(0, 0, 0);
        yPos += 12;

        doc.setFont('helvetica', 'normal');
        predictionData.maintenance_checks.recent_maintenance.forEach((item) => {
          checkNewPage(20);
          let formattedDate = 'N/A';
          if (item.date) {
            try {
              const dateObj = new Date(item.date);
              if (!isNaN(dateObj.getTime())) {
                formattedDate = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
              } else {
                formattedDate = item.date;
              }
            } catch (e) {
              formattedDate = item.date;
            }
          }
          const description = cleanText(item.description) || 'No description';
          const smuText = item.smu ? `${item.smu.toLocaleString()} hrs` : 'N/A hrs';
          const fullText = `${description} - At ${smuText} on ${formattedDate}`;
          const descText = doc.splitTextToSize(fullText, pageWidth - 2 * margin - 20);

          descText.forEach((line, lineIndex) => {
            checkNewPage(10);
            const prefix = lineIndex === 0 ? '- ' : '  ';
            doc.text(prefix + line, margin + 5, yPos);
            yPos += lineHeight;
          });
          yPos += 2;
        });
        yPos += 5;
      }
    }

    // Recommendations
    if (predictionData.recommendations && predictionData.recommendations.length > 0) {
      checkNewPage(50);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Actionable Recommendations (${predictionData.recommendations.length} total)`, margin, yPos);
      yPos += 10;

      predictionData.recommendations.forEach((rec, index) => {
        checkNewPage(30);
        const priorityColors = {
          'Critical': [254, 226, 226],
          'High': [255, 237, 213],
          'Medium': [254, 249, 195],
          'Low': [220, 252, 231]
        };
        const bgColor = priorityColors[rec.priority] || [245, 245, 245];

        const action = cleanText(rec.action) || 'No action specified';
        const timeline = cleanText(rec.timeline) || 'N/A';
        const impact = cleanText(rec.impact) || 'N/A';

        const actionText = doc.splitTextToSize(`${index + 1}. [${rec.priority}] ${action}`, pageWidth - 2 * margin - 10);
        const detailText = doc.splitTextToSize(`Timeline: ${timeline} | Impact: ${impact}`, pageWidth - 2 * margin - 10);

        const boxHeight = Math.max(18, (actionText.length + detailText.length) * lineHeight + 6);

        doc.setFillColor(...bgColor);
        doc.rect(margin, yPos - 3, pageWidth - 2 * margin, boxHeight, 'F');

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(actionText, margin + 3, yPos + 3);
        yPos += actionText.length * lineHeight + 2;

        doc.setFont('helvetica', 'normal');
        doc.text(detailText, margin + 3, yPos);
        yPos += detailText.length * lineHeight + 5;
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
    doc.text(
        `Page ${i} of ${pageCount} | Maintenance AI Agent | Data range: ${dataRangeLabel}`,
        pageWidth / 2,
        285,
        { align: 'center' }
      );
    }

    return doc;
  };

  const generatePredictionsPDF = () => {
    setIsGeneratingPdf(true);
    try {
      const doc = createPDFBlob();
      if (!doc) {
        notify({ type: 'warning', title: 'No data loaded', message: 'Please load prediction data first.' });
        return;
      }
      doc.save(`${selectedUnit}_Maintenance_Prediction_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      notify({ type: 'error', title: 'PDF failed', message: 'Error generating PDF.' });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleSaveToStorage = async () => {
    setIsSavingToStorage(true);
    try {
      const doc = createPDFBlob();
      if (!doc) return;

      // Convert JS PDF to Blob
      const pdfBlob = doc.output('blob');
      const file = new File([pdfBlob], `${selectedUnit}_Prediction_${new Date().getTime()}.pdf`, { type: 'application/pdf' });

      if (uploadToStorage) {
        await uploadToStorage(file, 'Predictions');
        notify({ type: 'success', title: 'Saved', message: 'Prediction report saved to Storage Hub.' });
      } else {
        console.error('uploadToStorage function not provided');
        notify({ type: 'warning', title: 'Storage unavailable', message: 'Storage functionality not available.' });
      }
    } catch (error) {
      console.error('Error saving to storage:', error);
      notify({ type: 'error', title: 'Save failed', message: 'Failed to save report to storage.' });
    } finally {
      setIsSavingToStorage(false);
    }
  };

  const getRiskVisual = (riskLevel, hasPrediction) => {
    if (!hasPrediction) {
      return {
        bgColor: 'bg-slate-300',
        borderColor: 'border-slate-400',
        hoverColor: 'hover:bg-slate-400',
      };
    }
    switch (String(riskLevel || '').toLowerCase()) {
      case 'low':
        return { bgColor: 'bg-green-500', borderColor: 'border-green-600', hoverColor: 'hover:bg-green-600' };
      case 'medium':
        return { bgColor: 'bg-yellow-500', borderColor: 'border-yellow-600', hoverColor: 'hover:bg-yellow-600' };
      case 'high':
        return { bgColor: 'bg-orange-500', borderColor: 'border-orange-600', hoverColor: 'hover:bg-orange-600' };
      case 'critical':
        return { bgColor: 'bg-red-500', borderColor: 'border-red-600', hoverColor: 'hover:bg-red-600' };
      default:
        return { bgColor: 'bg-slate-300', borderColor: 'border-slate-400', hoverColor: 'hover:bg-slate-400' };
    }
  };

  const isMetaLoading = (meta) => {
    const status = String(meta?.status || '');
    return ['queued', 'loading_summary', 'loading_advisory'].includes(status);
  };

  const isRefreshingFromCache = (meta) => {
    const status = String(meta?.status || '');
    return meta?.source === 'cache' && ['queued', 'loading_summary', 'loading_advisory', 'retry_wait'].includes(status);
  };

  const renderIntelligentUnitPredictions = () => {
    const riskRank = { critical: 0, high: 1, medium: 2, low: 3, unknown: 4 };
    const unitsWithRisk = availableUnits.map((unit) => {
      const normalizedRisk = normalizeRiskLevel(unitPredictions[unit]?.risk_level);
      const numberPart = parseInt(String(unit).replace(/[^0-9]/g, ''), 10);
      return {
        unit,
        risk: normalizedRisk,
        numberPart: Number.isFinite(numberPart) ? numberPart : 9999,
      };
    });

    const sortedUnits = [...unitsWithRisk].sort((a, b) => {
      const riskCmp = (riskRank[a.risk] ?? 99) - (riskRank[b.risk] ?? 99);
      if (riskCmp !== 0) return riskCmp;
      return a.numberPart - b.numberPart;
    });

    const filteredUnits = sortedUnits.filter((item) =>
      activeRiskFilter === 'all' ? true : item.risk === activeRiskFilter
    );
    const mobileLimit = 20;
    const displayedUnits = isMobile
      ? (
        !showAllUnitsMobile
          ? filteredUnits.slice(0, mobileLimit).map((item) => item.unit)
          : filteredUnits.map((item) => item.unit)
      )
      : filteredUnits.map((item) => item.unit);
    const hiddenCount = Math.max(0, filteredUnits.length - displayedUnits.length);
    const selectedPrediction = selectedUnit ? unitPredictions[selectedUnit] : null;
    const selectedMeta = selectedUnit ? (unitPredictionMeta[selectedUnit] || {}) : {};
    const selectedHasDetailedPrediction = hasDetailedPredictionPayload(selectedPrediction);

    return (
      <div className="space-y-6">
        <div className="rounded-xl border p-4 sm:p-6" style={{ background: 'linear-gradient(120deg, var(--bg-tertiary) 0%, var(--card-bg) 100%)', borderColor: 'var(--card-border)' }}>
          <div className="flex flex-col gap-3 mb-2">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <div className="p-2 rounded-lg" style={{ background: 'var(--status-info-bg)' }}>
                <Settings className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--status-info)' }} />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold break-words" style={{ color: 'var(--accent-text)' }}>Unplanned Maintenance Predictions & Advisory</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={generatePredictionsPDF}
                disabled={isGeneratingPdf || !unitPredictions[selectedUnit]}
                className={`inline-flex items-center px-3 py-2 rounded-lg text-base font-semibold transition-colors flex-shrink-0 border ${isGeneratingPdf || !unitPredictions[selectedUnit]
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                title={!unitPredictions[selectedUnit] ? 'Load prediction data first' : 'Download as PDF'}
              >
                <Download className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="whitespace-nowrap">{isGeneratingPdf ? 'Generating...' : 'Download PDF'}</span>
              </button>
              <button
                onClick={handleSaveToStorage}
                disabled={isSavingToStorage || !unitPredictions[selectedUnit]}
                className={`inline-flex items-center px-3 py-2 rounded-lg text-base font-semibold transition-colors flex-shrink-0 border ${isSavingToStorage || !unitPredictions[selectedUnit]
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                title="Save PDF to Storage Hub"
              >
                <Save className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="whitespace-nowrap">{isSavingToStorage ? 'Saving...' : 'Save to Storage'}</span>
              </button>
              <button
                onClick={() => setShowMethodology(!showMethodology)}
                className="inline-flex items-center text-base px-3 py-2 rounded-lg flex-shrink-0 border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                title="How we predict"
              >
                <Info className="h-4 w-4 mr-1.5 flex-shrink-0" />
                <span className="whitespace-nowrap">How we predict</span>
                {showMethodology ? <ChevronUp className="h-4 w-4 ml-1 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 ml-1 flex-shrink-0" />}
              </button>
            </div>
          </div>
          {/* Methodology Dropdown */}
          {showMethodology && (
            <div className="mt-4 p-4 rounded-lg border bg-white" style={{ borderColor: 'var(--card-border)' }}>
              <h4 className="text-lg font-semibold mb-3" style={{ color: 'var(--accent-text)' }}>How we predict</h4>
              <ul className="space-y-2 text-base mb-4" style={{ color: 'var(--accent-text-subtle)' }}>
                <li><strong style={{ color: 'var(--accent-text)' }}>1. Historical data:</strong> We use your maintenance records to find recurring events and frequency patterns per unit.</li>
                <li><strong style={{ color: 'var(--accent-text)' }}>2. Trends:</strong> We analyze trends in maintenance frequency over time to estimate future needs.</li>
                <li><strong style={{ color: 'var(--accent-text)' }}>3. Patterns:</strong> We identify the most common recent events per unit to predict likely future problems.</li>
                <li><strong style={{ color: 'var(--accent-text)' }}>4. Score and risk:</strong> Unit Truck Health Score is computed from weighted risk drivers, then mapped to risk color bands.</li>
                <li><strong style={{ color: 'var(--accent-text)' }}>5. Driver weights:</strong> Higher-than-usual event volume 55% (top driver), trend direction 30%, and short time to likely failure 15%.</li>
                <li><strong style={{ color: 'var(--accent-text)' }}>Note:</strong> Driver weights are calibrated using non-negative ridge regression (L2, lambda=0.35) with a time-split slope-stability drift test (max drift &lt;= 0.20).</li>
              </ul>
              <h5 className="font-semibold mb-2 text-base" style={{ color: 'var(--accent-text)' }}>Risk colors</h5>
              <ul className="space-y-2 text-base" style={{ color: 'var(--accent-text-subtle)' }}>
                <li className="flex items-start gap-2"><span className="w-3 h-3 rounded bg-green-500 flex-shrink-0 mt-0.5" /><span><strong style={{ color: 'var(--accent-text)' }}>Green (low):</strong> Health score 85-95.</span></li>
                <li className="flex items-start gap-2"><span className="w-3 h-3 rounded bg-yellow-500 flex-shrink-0 mt-0.5" /><span><strong style={{ color: 'var(--accent-text)' }}>Yellow (medium):</strong> Health score 75-84.</span></li>
                <li className="flex items-start gap-2"><span className="w-3 h-3 rounded bg-orange-500 flex-shrink-0 mt-0.5" /><span><strong style={{ color: 'var(--accent-text)' }}>Orange (high):</strong> Health score 60-74.</span></li>
                <li className="flex items-start gap-2"><span className="w-3 h-3 rounded bg-red-500 flex-shrink-0 mt-0.5" /><span><strong style={{ color: 'var(--accent-text)' }}>Red (critical):</strong> Health score 40-59.</span></li>
              </ul>
              <p className="text-base mt-3 pt-3 border-t" style={{ color: 'var(--accent-text-subtle)', borderColor: 'var(--card-border)' }}>
                Higher score means healthier unit. Lower score means higher maintenance risk.
              </p>
            </div>
          )}
        </div>

        {/* Unit Risk Indicator Grid - min-w-0 so grid can shrink and wrap; overflow-visible so content not clipped */}
        <div className="p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 min-w-0 overflow-visible" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <label className="text-base font-semibold" style={{ color: 'var(--accent-text)' }}>Unit Risk Indicators</label>
            <div className="text-base flex-shrink-0" style={{ color: 'var(--accent-text-subtle)' }}>
              Data range: {dataRangeLabel}
            </div>
          </div>

          {/* Risk Legend (desktop only) */}
          {!isMobile && (
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 text-sm">
              {[
                { key: 'critical', label: 'Critical Risk', dot: 'bg-red-500 border-red-600' },
                { key: 'high', label: 'High Risk', dot: 'bg-orange-500 border-orange-600' },
                { key: 'medium', label: 'Medium Risk', dot: 'bg-yellow-500 border-yellow-600' },
                { key: 'low', label: 'Low Risk', dot: 'bg-green-500 border-green-600' },
              ].map((riskOption) => (
                <button
                  key={riskOption.key}
                  type="button"
                  onClick={() => {
                    setActiveRiskFilter((prev) => (prev === riskOption.key ? 'all' : riskOption.key));
                    setShowAllUnitsMobile(false);
                  }}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors ${
                    activeRiskFilter === riskOption.key ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                  }`}
                  title={`Show ${riskOption.label} units`}
                >
                  <div className={`w-4 h-4 rounded border ${riskOption.dot}`}></div>
                  <span className={activeRiskFilter === riskOption.key ? 'text-blue-700 font-semibold' : 'text-gray-600'}>
                    {riskOption.label}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setActiveRiskFilter('all');
                  setShowAllUnitsMobile(false);
                }}
                className={`px-2 py-1 rounded-md border transition-colors ${
                  activeRiskFilter === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'
                }`}
                title="Show all units"
              >
                All
              </button>
            </div>
          )}

          {/* Mobile controls for dense unit lists */}
          {isMobile && (
            <div className="mb-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'critical', label: 'Critical' },
                  { key: 'high', label: 'High' },
                  { key: 'medium', label: 'Medium' },
                  { key: 'low', label: 'Low' },
                ].map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => {
                      setActiveRiskFilter(filter.key);
                      setShowAllUnitsMobile(false);
                    }}
                    className={`px-2.5 py-1 rounded-full text-sm font-semibold border transition-colors ${
                      activeRiskFilter === filter.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between text-base text-gray-500">
                <span>Showing {displayedUnits.length} of {filteredUnits.length} units</span>
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllUnitsMobile(true)}
                    className="text-blue-600 font-semibold"
                  >
                    Show all
                  </button>
                )}
                {hiddenCount === 0 && filteredUnits.length > mobileLimit && (
                  <button
                    type="button"
                    onClick={() => setShowAllUnitsMobile(false)}
                    className="text-blue-600 font-semibold"
                  >
                    Show less
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Unit Squares Grid */}
          <div className={isMobile
            ? 'grid grid-cols-4 gap-2 min-w-0 overflow-visible -m-1 p-3 min-h-[3.5rem]'
            : 'flex flex-wrap gap-2 min-w-0 overflow-x-auto overflow-y-visible -m-1 p-3 min-h-[3.5rem]'
          }>
            {displayedUnits.map((unit) => {
              // Get risk level from prediction data - only if available
              const prediction = unitPredictions[unit];
              const meta = unitPredictionMeta[unit] || {};
              const isLoading = Boolean(loadingStates[`intelligent_${unit}`]) || isMetaLoading(meta);
              const hasPrediction = !!prediction && prediction.risk_level;

              const { bgColor, borderColor, hoverColor } = getRiskVisual(prediction?.risk_level, hasPrediction);
              let riskLevel = 'Not Loaded';

              if (hasPrediction) riskLevel = prediction.risk_level;

              const isSelected = selectedUnit === unit;

              return (
                <button
                  type="button"
                  key={unit}
                  onClick={() => setSelectedUnit(unit)}
                  className={`
                    relative w-12 h-12 rounded-xl border cursor-pointer transition-all duration-200
                    ${bgColor} ${borderColor} ${hoverColor}
                    ${isSelected ? 'ring-2 ring-blue-400 ring-offset-2 scale-105 shadow-lg' : 'shadow-sm'}
                    flex items-center justify-center text-white font-semibold text-[0.95rem]
                    group
                    ${isLoading ? 'opacity-75' : ''}
                    focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2
                  `}
                  title={`${unit}${hasPrediction ? ` - ${riskLevel} Risk (${prediction.confidence_score || 'N/A'} confidence)` : ' - Click to load prediction'}`}
                  aria-pressed={isSelected}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                  ) : (
                    <span className="text-white font-bold text-[0.95rem]">{unit.replace('TK', '')}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Selected Unit Info */}
          {selectedUnit && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-base font-medium" style={{ color: 'var(--accent-text-subtle)' }}>Selected:</span>
                  <span className="text-base font-semibold" style={{ color: 'var(--accent-text)' }}>{selectedUnit}</span>
                  {isRefreshingFromCache(selectedMeta) && (
                    <span className="text-base px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                      Refreshing cached data
                    </span>
                  )}
                  {selectedPrediction && (
                    <span className={`text-base px-2 py-1 rounded-full ${selectedPrediction.risk_level === 'Critical' || selectedPrediction.risk_level === 'High'
                      ? 'bg-orange-100 text-orange-700'
                      : selectedPrediction.risk_level === 'Medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                      }`}>
                      {selectedPrediction.risk_level} Risk
                    </span>
                  )}
                </div>
                {!selectedHasDetailedPrediction && !isMetaLoading(selectedMeta) && (
                  <button
                    onClick={() => fetchIntelligentUnitPrediction(selectedUnit, false, 'summary')}
                    className="text-base text-blue-600 hover:text-blue-800 font-semibold hover:bg-blue-50 px-3 py-1 rounded-lg transition-colors"
                  >
                    Load Full Prediction
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Selected Unit Prediction */}
        <div className="w-full">
          <IntelligentUnitPrediction
            key={selectedUnit}
            unitName={selectedUnit}
            predictionData={unitPredictions[selectedUnit]}
            predictionMeta={unitPredictionMeta[selectedUnit]}
            onGetPrediction={fetchIntelligentUnitPrediction}
          />
        </div>
      </div>
    );
  };

  const renderSeasonalAnalysis = () => {
    // Check loading state
    if (loadingStates.seasonal || seasonalAnalysis?.initializing) {
      return (
        <div className="space-y-6 animate-pulse">
          {/* Header Skeleton */}
          <div className="h-24 rounded-xl bg-gray-200 w-full mb-6"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Insights Skeleton */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-64">
              <div className="h-6 w-1/3 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="h-2 w-2 rounded-full bg-gray-200"></div>
                    <div className="h-4 bg-gray-100 rounded w-full"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Patterns Skeleton */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-64">
              <div className="h-6 w-1/3 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex justify-between items-center p-2">
                    <div className="h-4 w-1/4 bg-gray-100 rounded"></div>
                    <div className="h-4 w-1/4 bg-gray-100 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!seasonalAnalysis || seasonalAnalysis.error) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-base">Seasonal analysis not available</p>
            {seasonalAnalysis?.error && (
              <p className="text-xs text-red-400 mt-1">{seasonalAnalysis.error}</p>
            )}
          </div>
        </div>
      );
    }

    const monthlyEntries = seasonalAnalysis.monthly_patterns
      ? Object.entries(seasonalAnalysis.monthly_patterns)
      : [];
    const totalSlides = 2;
    const goToSlide = (index) => {
      if (index < 0) return setSeasonalSlide(totalSlides - 1);
      if (index >= totalSlides) return setSeasonalSlide(0);
      setSeasonalSlide(index);
    };

    return (
      <div className="space-y-6">
        <div className="p-6 rounded-xl border" style={{ background: 'linear-gradient(120deg, var(--bg-tertiary) 0%, var(--card-bg) 100%)', borderColor: 'var(--card-border)' }}>
          <div className="flex items-center mb-2">
            <BarChart3 className="h-5 w-5 mr-2 text-blue-700" />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>Seasonal Pattern Analysis</h3>
          </div>
          <p className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>
            Peak month: <span className="font-semibold">{seasonalAnalysis.peak_month}</span> |
            Peak day: <span className="font-semibold">{seasonalAnalysis.peak_day_of_week}</span>
          </p>
        </div>

        {isMobile ? (
          <div className="rounded-xl border p-4 sm:p-6" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold" style={{ color: 'var(--accent-text)' }}>
                {seasonalSlide === 0 ? 'Seasonal Insights' : 'Monthly Patterns'}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToSlide(seasonalSlide - 1)}
                  className="p-1.5 rounded-lg border transition-colors"
                  style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text-subtle)' }}
                  aria-label="Previous seasonal slide"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => goToSlide(seasonalSlide + 1)}
                  className="p-1.5 rounded-lg border transition-colors"
                  style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text-subtle)' }}
                  aria-label="Next seasonal slide"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${seasonalSlide * 100}%)` }}
              >
                <div className="w-full flex-shrink-0">
                  <div className="space-y-3">
                    {seasonalAnalysis.seasonal_insights?.map((insight, index) => (
                      <div key={index} className="flex items-start p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                        <span className="text-purple-500 mr-2 mt-1">&bull;</span>
                        <span className="text-sm" style={{ color: 'var(--accent-text)' }}>{insight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="w-full flex-shrink-0">
                  <div className="space-y-2">
                    {monthlyEntries.map(([month, count]) => {
                      let monthName;
                      if (!isNaN(parseInt(month))) {
                        monthName = new Date(2000, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' });
                      } else {
                        monthName = month;
                      }

                      return (
                        <div key={month} className="flex justify-between items-center p-2 rounded border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                          <span className="text-sm" style={{ color: 'var(--accent-text)' }}>{monthName}</span>
                          <span className="text-sm font-semibold" style={{ color: 'var(--accent-text)' }}>{count} events</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2">
              {[0, 1].map((idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => goToSlide(idx)}
                  className={`h-2.5 rounded-full transition-all ${seasonalSlide === idx ? 'w-6 bg-blue-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400'}`}
                  aria-label={`Go to seasonal slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border p-4 sm:p-6" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
              <div className="text-sm font-semibold mb-4" style={{ color: 'var(--accent-text)' }}>Seasonal Insights</div>
              <div className="space-y-3">
                {seasonalAnalysis.seasonal_insights?.map((insight, index) => (
                  <div key={index} className="flex items-start p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                    <span className="text-purple-500 mr-2 mt-1">&bull;</span>
                    <span className="text-sm" style={{ color: 'var(--accent-text)' }}>{insight}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border p-4 sm:p-6" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
              <div className="text-sm font-semibold mb-4" style={{ color: 'var(--accent-text)' }}>Monthly Patterns</div>
              <div className="space-y-2">
                {monthlyEntries.map(([month, count]) => {
                  let monthName;
                  if (!isNaN(parseInt(month))) {
                    monthName = new Date(2000, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' });
                  } else {
                    monthName = month;
                  }

                  return (
                    <div key={month} className="flex justify-between items-center p-2 rounded border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                      <span className="text-sm" style={{ color: 'var(--accent-text)' }}>{monthName}</span>
                      <span className="text-sm font-semibold" style={{ color: 'var(--accent-text)' }}>{count} events</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };



  return (
    <div className="space-y-8">

      {/* Header / Intro */}
      <section className="home-hero card p-6 sm:p-8">
        <div className="flex items-center">
          <div className="p-3 rounded-xl mr-4" style={{ background: 'var(--status-info-bg)' }}>
            <Sparkles className="w-8 h-8" style={{ color: 'var(--status-info)' }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>Predictions & Forecasting</h2>
            <p className="mt-1" style={{ color: 'var(--accent-text-subtle)' }}>
              Data-driven predictions focused on preventing unplanned failures with actionable insights and prioritized recommendations
            </p>
          </div>
        </div>
      </section>

      {/* Unit Predictions - keep desktop card, remove outer shell on mobile for more usable width */}
      <section className={isMobile ? 'px-0 py-0' : 'home-section card'}>
        <div className="home-section-header">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg" style={{ background: 'var(--status-info-bg)' }}>
                <Settings className="h-4 w-4" style={{ color: 'var(--status-info)' }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: 'var(--accent-text)' }}>Unit Predictions</h3>
            </div>
            <p className="text-sm mt-2" style={{ color: 'var(--accent-text-subtle)' }}>
              Select a truck unit to inspect risk level, confidence, and AI recommendations.
            </p>
          </div>
        </div>
        <div className={isMobile ? 'pt-2' : 'home-section-body'}>
          {renderIntelligentUnitPredictions()}
        </div>
      </section>

      {/* Seasonal Analysis */}
      <section className="home-section card">
        <div className="home-section-header">
          <div>
            <h3 className="text-lg font-bold flex items-center" style={{ color: 'var(--accent-text)' }}>
              <div className="p-2 bg-purple-50 rounded-lg mr-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
              </div>
              Seasonal Pattern Analysis
            </h3>
            <p className="text-sm mt-2" style={{ color: 'var(--accent-text-subtle)' }}>
              Identify seasonal trends and patterns in maintenance events
            </p>
          </div>
        </div>
        <div className="home-section-body">
          {renderSeasonalAnalysis()}
        </div>
      </section>

    </div>
  );
};

export default PredictionsDashboard;

